import { LatLngBounds } from 'leaflet';
import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import { GeoJSON, useLeaflet } from 'react-leaflet';
import { SeaRegions } from '../DetailedSeaRegions';

const highlightedRegionColor = '#0C6BC4';
const regionColor = '#647078';

export interface SeaRegionLayerProps {
  region: { key: number };
  setRegion: (val: { key: number }) => void;
  zIndex: number;
}

export const SeaRegionLayer: React.FunctionComponent<SeaRegionLayerProps> = ({
  region,
  setRegion,
}) => {
  const key = region.key;
  const keyRef = useRef(key);
  keyRef.current = key;
  const { map } = useLeaflet();

  const styleFeature = useCallback(
    (feature) => {
      const id = feature?.properties?.id;
      if (id === keyRef.current) {
        return {
          color: highlightedRegionColor,
          weight: 2,
          fillColor: 'transparent',
        };
      }
      return { color: regionColor, weight: 2, fillColor: 'transparent' };
    },
    [keyRef]
  );

  const featureBounds = useRef<{ [key: number]: LatLngBounds }>({});

  const onEachFeature = useCallback(
    (feature, layer) => {
      featureBounds.current[feature.properties.id] = layer.getBounds();
    },
    [featureBounds]
  );

  const onClick = useCallback(
    (event) => {
      const id = event?.layer?.feature?.properties?.id;
      if (id) {
        setRegion({ key: id });
      }
    },
    [setRegion]
  );

  const sortedSeaRegions = useMemo(() => {
    const ret = { ...SeaRegions };
    ret.features = ret.features.concat([]);
    const val = (a) => (a.properties.id === region.key ? 1 : 0);
    ret.features.sort((a, b) => val(a) - val(b));
    return ret;
  }, [region]);

  useEffect(() => {
    if (!map) return;
    const currentBounds = featureBounds.current[region.key];
    if (!currentBounds) return;
    if (map.getBounds().contains(currentBounds)) return;
    map.fitBounds(featureBounds.current[region.key], { animate: false });
  }, [map, region.key, featureBounds.current[region.key]]);

  return (
    <GeoJSON
      key={region.key}
      onEachFeature={onEachFeature}
      data={sortedSeaRegions as any}
      style={styleFeature}
      onClick={onClick}
    ></GeoJSON>
  );
};
